"use client";

import React, { useMemo } from "react";
import { useCompetitionHomeData } from "../../../context/CompetitionHomeDataContext";
import { TeamLogosScroll } from "../../../partials/teams/TeamLogosScroll";
import { MatchTabbedList } from "../../../partials/matches/MatchTabbedList";
import { SeasonStatsCards } from "../../../partials/stats/SeasonStatsCards";
import { LeadersTables } from "../../../partials/players/LeadersTables";
import { PromoSlot } from "@/features/promo-slots/PromoSlot";
import { AdSlot } from "@/features/ads/AdSlot";
import { SectionStrip, type BrandedStyle } from "@/components/layout/SectionStrip";
import { HOME_TEAMS, HOME_HERO, HOME_MATCHES, HOME_LEADERS, HOME_STATS } from "@/features/theme/sectionKeys";
import { getEmbedUrl } from "@/views/video/utils/getEmbedUrl";
import { LiveTableTab } from "@/views/standings/tabs/LiveTableTab";
import { InternationalStandings } from "@/views/standings/overrides/international/InternationalStandings";

interface FootballCompetitionHomeDataOnlyLayoutProps {
    videoShortsSlot?: React.ReactNode;
    videoHighlightsSlot?: React.ReactNode;
    sectionStyles?: Record<string, BrandedStyle>;
}

export function FootballCompetitionHomeDataOnlyLayout({
    videoShortsSlot,
    videoHighlightsSlot,
    sectionStyles,
}: FootballCompetitionHomeDataOnlyLayoutProps = {}) {
    const {
        promos,
        leaders,
        allTeams,
        allStageStandings,
        featuredPosts,
        standings,
        homeSlices,
        competitionStats,
        goalDistribution,
        hasCompetitionStats,
    } = useCompetitionHomeData();

    const heroVideo = useMemo(() => {
        const videoPost = featuredPosts?.find((post) => post.videoEmbed?.url);
        if (!videoPost?.videoEmbed) return null;
        const embedUrl = getEmbedUrl(videoPost.videoEmbed);
        if (!embedUrl && !videoPost.videoEmbed.embedCode) return null;
        return { embedUrl, title: videoPost.title, embedCode: videoPost.videoEmbed.embedCode };
    }, [featuredPosts]);

    return (
        <div className="min-h-screen bg-background">
            <main className="max-w-350 mx-auto px-4 py-6 space-y-10">
                {/* Ad: content-top */}
                <AdSlot id="content-top" />

                {/* Row 1: Teams */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_TEAMS]}>
                    <TeamLogosScroll teams={allTeams} />
                </SectionStrip>

                {/* Featured Video from Featured Posts */}
                {heroVideo && (
                    <SectionStrip brandedStyle={sectionStyles?.[HOME_HERO]}>
                        <div className="relative w-full aspect-video rounded-lg overflow-hidden shadow-md">
                            {heroVideo.embedUrl ? (
                                <iframe
                                    src={heroVideo.embedUrl}
                                    className="absolute inset-0 w-full h-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    title={heroVideo.title}
                                />
                            ) : heroVideo.embedCode ? (
                                <div
                                    className="absolute inset-0 w-full h-full [&>iframe]:w-full [&>iframe]:h-full"
                                    dangerouslySetInnerHTML={{ __html: heroVideo.embedCode }}
                                />
                            ) : null}
                        </div>
                    </SectionStrip>
                )}

                {/* Row 2: Full Standings (replaces news + compact standings) */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_HERO]}>
                    {homeSlices.internationalGroups ? (
                        <InternationalStandings
                            standings={standings}
                            groupedStandings={homeSlices.internationalGroups}
                            groupMatches={homeSlices.groupMatches}
                            hideHeader
                        />
                    ) : (
                        <LiveTableTab allStageStandings={allStageStandings} standings={standings} />
                    )}
                </SectionStrip>

                {/* Promo slot 0 (preserves promo slot from Classic's news column) */}
                {promos[0] && <PromoSlot row={promos[0]} />}

                {/* Ad: mid-scroll-1 */}
                <AdSlot id="mid-scroll-1" />

                {/* Video Shorts */}
                {videoShortsSlot}

                {/* Row 3: Latest Results and Upcoming Matches */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_MATCHES]}>
                    <MatchTabbedList
                        finishedMatches={homeSlices.lastFinished}
                        upcomingMatches={homeSlices.nextUpcoming}
                    />
                </SectionStrip>

                {/* Ad: mid-scroll-2 */}
                <AdSlot id="mid-scroll-2" />

                {/* Video Highlights */}
                {videoHighlightsSlot}

                {/* Promo slot 1 */}
                <PromoSlot row={promos[1] ?? null} />

                {/* Row 5: Leaders */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_LEADERS]}>
                    <LeadersTables leaders={leaders} />
                </SectionStrip>

                {/* Ad: mid-scroll-3 */}
                <AdSlot id="mid-scroll-3" />

                {/* Promo slot 2 */}
                <PromoSlot row={promos[2] ?? null} />

                {/* Row 6: Season Stats */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_STATS]}>
                    {hasCompetitionStats && (
                        <SeasonStatsCards
                            competitionStats={competitionStats}
                            goalDistribution={goalDistribution}
                            teamWithMostWins={homeSlices.teamWithMostWins}
                        />
                    )}
                </SectionStrip>

                {/* Promo slot 3 */}
                <PromoSlot row={promos[3] ?? null} />

                {/* Ad: content-bottom */}
                <AdSlot id="content-bottom" />
            </main>
        </div>
    );
}
